import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, timeout, filter, take, switchMap } from 'rxjs/operators';

import { AppConfigService } from '~/core/services/appconfig.service';
import { AppConfig } from '~/core/models/appconfig';
import { WellsApiService } from '~/core/services/msal_token.service';

const TIMEOUT: number = 30000;
const DEFAULT_DASHBOARD_START_DATE = '2026-09-03';

function getCurrentDate(): string {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

export interface Workshop {
    id: string;
    mode: string;
    file_count: number;
}

export interface WorkshopFile {
    name: string;
    size_kb: number;
    type: string;
}

export interface Mapping {
    name: string;
    size_kb: number;
}

export interface PipelineRunRequest {
    workshop_id: string;
}

export interface PipelineRunResponse {
    run_id: string;
    status: string;
    workshop_id: string;
}

export interface PipelineLog {
    timestamp: string;
    level: string;
    event: string;
    message: string;
    data: any;
}

export interface PipelineStatusResponse {
    run_id: string;
    status: string;
    current_step: string | null;
    progress_pct: number;
    transcripts_total: number;
    transcripts_done: number;
    pbls_generated: number;
    fit_count: number;
    gap_count: number;
    error: string | null;
    logs: PipelineLog[];
}

export interface PBLItem {
    pbl_id: string;
    title: string;
    fit_gap: string;
    fit_gap_rb: string;
    rb_confidence: number;
    rb_matched_ref_id: string;
    rb_matched_ref_title: string;
    description: string;
    priority: string;
    l3_scope: string;
    workshop_origin: string[];
    evidence: any;
}

export interface Filters {
    counterparties: string[];
    subjects: string[];
    classifications: string[];
    escalations: string[];
}

export interface DashboardSummary {
    ndasInFlight: number;
    mailChains: number;
    automatedPct: number;
    avgInboxToDraftHours: number;
    reviewNDARequests: number;
    newNDARequests: number;
    hardReviewCount: number;
}

export interface Sender {
    email: string;
    name: string;
    count: number;
    lastSeen: string;
}

export interface EmailVolumeByDay {
    date: string;
    count: number;
}

export interface EmailByDayOfWeek {
    day: string;
    count: number;
}

export interface DashboardLegalSendersResponse {
    senders: Sender[];
    uniqueCount: number;
    totalCount: number;
    avgMailsPerSender: number;
    emailVolumeByDay: EmailVolumeByDay[];
    emailsByDayOfWeek: EmailByDayOfWeek[];
}

export interface VolumeTrendItem {
    period: string;
    type: string;
    count: number;
}

export interface ClassificationSplitItem {
    type: string;
    count: number;
}

export interface TemplateDistributionItem {
    ndaType: string;
    count: number;
}

export interface CounterpartyNegotiationItem {
    counterparty: string;
    chains: number;
}

export interface DashboardLegalVolumeResponse {
    volumeTrend: VolumeTrendItem[];
    classificationSplit: ClassificationSplitItem[];
    templateDistribution: TemplateDistributionItem[];
    counterpartyNegotiations: CounterpartyNegotiationItem[];
}

export interface EscalationDistributionItem {
    escalation: string;
    count: number;
}

export interface AgingQueueItem {
    age: string;
    escalation: string;
    count: number;
}

export interface EscalationByCounterpartyItem {
    counterparty: string;
    escalation: string;
    count: number;
}

export interface FrictionClausesItem {
    ruleId: string;
    ruleName: string;
    events: number;
}

export interface EscalationTrendItem {
    period: string;
    escalation: string;
    count: number;
}

export interface EscalationCategoriesItem {
    category: string;
    level: string;
    count: number;
}

export interface DashboardLegalRiskResponse {
    escalationDistribution: EscalationDistributionItem[];
    agingQueue: AgingQueueItem[];
    escalationByCounterparty: EscalationByCounterpartyItem[];
    frictionClauses: FrictionClausesItem[];
    escalationTrend: EscalationTrendItem[];
    escalationCategories: EscalationCategoriesItem[];
}

@Injectable({
    providedIn: 'root'
})
export class HttpService {
    private get config(): AppConfig { return this.appconfigService.getConfig(); }
    private apiHeader: HttpHeaders = new HttpHeaders({ "accept": "application/json", "Content-Type": "application/json" });

    constructor(
        private http: HttpClient,
        private appconfigService: AppConfigService,
        private wellsApiService: WellsApiService
    ) {
        console.log('[HttpService] Initializing service');
    }

    /**
     * Get HTTP headers with Bearer token for authenticated API requests
     * Uses the Wells API token from WellsApiService
     * If token not available yet, waits for it
     */
    private getAuthHeaders(): HttpHeaders {
        try {
            return this.wellsApiService.getAuthHeaders();
        } catch (error) {
            // Token not available yet - use unauthenticated headers as fallback
            // The API calls will fail with 401, but this allows parallel execution
            console.warn('[HttpService] getAuthHeaders - Token not available yet, using basic headers');
            return this.apiHeader;
        }
    }


    /**
     * Generic GET method
     */
    public Get<T>(endpoint: string, headers: HttpHeaders = this.apiHeader, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.get<T>(`${this.config.apiUrl}/${endpoint}`, { headers: headers }).pipe(timeout(callTimeout));
    }

    /**
     * Generic POST method
     */
    public Post<T>(endpoint: string, body?: any, headers: HttpHeaders = this.apiHeader, callTimeout: number = TIMEOUT): Observable<T> {
        return this.http.post<T>(`${this.config.apiUrl}/${endpoint}`, body, { headers: headers }).pipe(timeout(callTimeout));
    }

    /**
     * Get list of workshops/work IDs for dropdown
     * GET /api/v1/workshops
     * @returns Observable with array of workshops containing id, mode, and file_count
     */
    public getWorkshops(): Observable<Workshop[]> {
        console.log('[API] getWorkshops - Fetching list of workshops');
        
        return this.http.get<Workshop[]>(`${this.config.apiUrl}/api/v1/workshops`, { headers: this.apiHeader }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getWorkshops - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Get files in a specific workshop
     * GET /api/v1/workshops/{workId}/files
     * @param workId The workshop ID
     * @returns Observable with array of files containing name, size_kb, and type
     */
    public getWorkshopFiles(workId: string): Observable<WorkshopFile[]> {
        console.log('[API] getWorkshopFiles - Fetching files for workshop:', workId);
        
        return this.http.get<WorkshopFile[]>(`${this.config.apiUrl}/api/v1/workshops/${workId}/files`, { headers: this.apiHeader }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getWorkshopFiles - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Get mappings/additional context files
     * GET /api/v1/mappings
     * @returns Observable with array of mappings containing name and size_kb
     */
    public getMappings(): Observable<Mapping[]> {
        console.log('[API] getMappings - Fetching mappings');
        
        return this.http.get<Mapping[]>(`${this.config.apiUrl}/api/v1/mappings`, { headers: this.apiHeader }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getMappings - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Fetch the list of user IDs from the API
     * GET /api/user_ids
     * response format : { user_ids: { user_id: string }[] }
     */
    public getUserIds(): Observable<{ user_ids: { user_id: string }[] }> {
        console.log('[API] getUserIds - Fetching user IDs');
        return this.http.get<{ user_ids: { user_id: string }[] }>(`${this.config.apiUrl}/api/user_ids`, { headers: this.apiHeader }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getUserIds - FAILED. Status:', error?.status);
                throw error;
            })
        );
    }

    /**
     * Run the pipeline for a workshop
     * POST /api/v1/pipeline/run
     * @param workshopId The workshop ID to run the pipeline for
     * @returns Observable with run_id, status, and workshop_id
     */
    public runPipeline(workshopId: string): Observable<PipelineRunResponse> {
        console.log('[API] runPipeline - Starting pipeline for workshop:', workshopId);
        
        const request: PipelineRunRequest = { workshop_id: workshopId };
        
        return this.http.post<PipelineRunResponse>(`${this.config.apiUrl}/api/v1/pipeline/run`, request, { headers: this.apiHeader }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] runPipeline - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Get the status of a running pipeline
     * GET /api/v1/pipeline/status/{run_id}
     * @param runId The pipeline run ID
     * @returns Observable with pipeline status, progress, and metrics
     */
    public getPipelineStatus(runId: string): Observable<PipelineStatusResponse> {
        console.log('[API] getPipelineStatus - Fetching status for run:', runId);
        
        return this.http.get<PipelineStatusResponse>(`${this.config.apiUrl}/api/v1/pipeline/status/${runId}`, { headers: this.apiHeader }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getPipelineStatus - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Get generated PBL results for a workshop
     * GET /api/v1/results/{workshop_id}
     * @param workshopId The workshop ID
     * @returns Observable with array of generated PBL items
     */
    public getResults(workshopId: string): Observable<PBLItem[]> {
        console.log('[API] getResults - Fetching generated results for workshop:', workshopId);
        
        return this.http.get<PBLItem[]>(`${this.config.apiUrl}/api/v1/results/${workshopId}`, { headers: this.apiHeader }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getResults - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Get pipeline logs for a running or completed pipeline
     * GET /api/v1/pipeline/logs/{run_id}
     * @param runId The pipeline run ID
     * @returns Observable with array of pipeline logs
     */
    public getPipelineLogs(runId: string): Observable<PipelineLog[]> {
        console.log('[API] getPipelineLogs - Fetching logs for run:', runId);
        
        return this.http.get<PipelineLog[]>(`${this.config.apiUrl}/api/v1/pipeline/logs/${runId}`, { headers: this.apiHeader }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getPipelineLogs - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Get filter options for dropdowns (counterparties, subjects, classifications, escalations)
     * GET /api/filters
     * @returns Observable with filters containing counterparties, subjects, classifications, and escalations
     */
    public getFilters(): Observable<Filters> {
        console.log('[API] getFilters - Fetching filter options');
        
        // Make the API call directly with auth headers (MSAL token is now used directly)
        return this.http.get<Filters>(`${this.config.apiUrl}/api/filters`, { headers: this.getAuthHeaders() }).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getFilters - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Get dashboard summary KPIs
     * GET /api/dashboard/summary
     * @param startDate Start date in format YYYY-MM-DD
     * @param endDate End date in format YYYY-MM-DD
     * @returns Observable with dashboard metrics (ndasInFlight, mailChains, automatedPct, etc.)
     */
    /**
     * Get dashboard summary data including KPIs
     * GET /api/dashboard/summary
     * @param fromDate Optional start date in YYYY-MM-DD format
     * @param toDate Optional end date in YYYY-MM-DD format
     * @param counterparty Optional counterparty filter
     * @returns Observable with dashboard summary KPIs
     */
    public getDashboardSummary(fromDate?: string, toDate?: string, counterparty?: string): Observable<DashboardSummary> {
        console.log('[API] getDashboardSummary - Fetching dashboard summary', { fromDate, toDate, counterparty });
        
        let url = `${this.config.apiUrl}/api/dashboard/summary`;
        const params = new URLSearchParams();
        const effectiveFromDate = fromDate || DEFAULT_DASHBOARD_START_DATE;
        const effectiveToDate = toDate || getCurrentDate();
        
        params.append('startDate', effectiveFromDate);
        params.append('endDate', effectiveToDate);
        if (counterparty) params.append('counterparty', counterparty);
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        return this.http.get<DashboardSummary>(
            url,
            { headers: this.getAuthHeaders() }
        ).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getDashboardSummary - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Get dashboard legal senders data including email volume and day of week statistics
     * GET /api/dashboard/legal/senders
     * @param fromDate Optional start date in YYYY-MM-DD format
     * @param toDate Optional end date in YYYY-MM-DD format
     * @param counterparty Optional counterparty filter
     * @returns Observable with senders list, email volume by day, and emails by day of week
     */
    public getDashboardLegalSenders(fromDate?: string, toDate?: string, counterparty?: string): Observable<DashboardLegalSendersResponse> {
        console.log('[API] getDashboardLegalSenders - Fetching legal dashboard senders data', { fromDate, toDate, counterparty });
        
        let url = `${this.config.apiUrl}/api/dashboard/legal/senders`;
        const params = new URLSearchParams();
        const effectiveFromDate = fromDate || DEFAULT_DASHBOARD_START_DATE;
        const effectiveToDate = toDate || getCurrentDate();
        
        params.append('fromDate', effectiveFromDate);
        params.append('toDate', effectiveToDate);
        if (counterparty) params.append('counterparty', counterparty);
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        return this.http.get<DashboardLegalSendersResponse>(
            url,
            { headers: this.getAuthHeaders() }
        ).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getDashboardLegalSenders - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Get dashboard legal volume data including volume trends, classifications, templates, and negotiations
     * GET /api/dashboard/legal/volume
     * @param fromDate Optional start date in YYYY-MM-DD format
     * @param toDate Optional end date in YYYY-MM-DD format
     * @param counterparty Optional counterparty filter
     * @returns Observable with volume trend, classification split, template distribution, and counterparty negotiations
     */
    public getDashboardLegalVolume(fromDate?: string, toDate?: string, counterparty?: string): Observable<DashboardLegalVolumeResponse> {
        console.log('[API] getDashboardLegalVolume - Fetching legal dashboard volume data', { fromDate, toDate, counterparty });
        
        let url = `${this.config.apiUrl}/api/dashboard/legal/volume`;
        const params = new URLSearchParams();
        const effectiveFromDate = fromDate || DEFAULT_DASHBOARD_START_DATE;
        const effectiveToDate = toDate || getCurrentDate();
        
        params.append('fromDate', effectiveFromDate);
        params.append('toDate', effectiveToDate);
        if (counterparty) params.append('counterparty', counterparty);
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        return this.http.get<DashboardLegalVolumeResponse>(
            url,
            { headers: this.getAuthHeaders() }
        ).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getDashboardLegalVolume - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }

    /**
     * Get dashboard legal risk data including escalation distribution, aging queue, and escalation trends
     * GET /api/dashboard/legal/risk
     * @param fromDate Optional start date in YYYY-MM-DD format
     * @param toDate Optional end date in YYYY-MM-DD format
     * @param counterparty Optional counterparty filter
     * @returns Observable with risk and escalation data
     */
    public getDashboardLegalRisk(fromDate?: string, toDate?: string, counterparty?: string): Observable<DashboardLegalRiskResponse> {
        console.log('[API] getDashboardLegalRisk - Fetching legal dashboard risk data', { fromDate, toDate, counterparty });
        
        let url = `${this.config.apiUrl}/api/dashboard/legal/risk`;
        const params = new URLSearchParams();
        const effectiveFromDate = fromDate || DEFAULT_DASHBOARD_START_DATE;
        const effectiveToDate = toDate || getCurrentDate();
        
        params.append('fromDate', effectiveFromDate);
        params.append('toDate', effectiveToDate);
        if (counterparty) params.append('counterparty', counterparty);
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        return this.http.get<DashboardLegalRiskResponse>(
            url,
            { headers: this.getAuthHeaders() }
        ).pipe(
            timeout(TIMEOUT),
            catchError(error => {
                console.error('[API] getDashboardLegalRisk - FAILED. Status:', error?.status, '| Message:', error?.message);
                throw error;
            })
        );
    }
}
